import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  BookingAction,
  BookingDetailResponse,
  BookingDto,
  BookingOverviewResponse,
  BookingSummariesResponse,
  BookingSourceType,
  BookingsListParams,
  BookingsListResponse,
  ConflictCheckResponse,
  HoldAction,
  HoldDetailResponse,
  HoldDto,
  HoldReleaseReasonType,
  HoldsListParams,
  HoldsListResponse,
  ParticipantAction,
  ParticipantType,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/bookings`;
const HOLDS = `${API_VERSION_PREFIX}/hq/booking-holds`;

function toQuery(
  params: BookingsListParams | HoldsListParams,
): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if (params.q) search.set("q", params.q);
  if (params.business_id) search.set("business_id", params.business_id);
  if (params.customer_user_id) {
    search.set("customer_user_id", params.customer_user_id);
  }
  if (params.status) search.set("status", params.status);
  if (params.market_id) search.set("market_id", params.market_id);
  if (params.bookable_resource_id) {
    search.set("bookable_resource_id", params.bookable_resource_id);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createBookingsApi(client: ApiClient) {
  return {
    list(params: BookingsListParams = {}) {
      return client.get<BookingsListResponse>(`${BASE}${toQuery(params)}`);
    },

    getDetail(bookingId: string) {
      return client.get<BookingDetailResponse>(`${BASE}/${bookingId}`);
    },

    /** Names, money and attendance for a page of bookings. */
    summaries(ids: string[]) {
      return client.post<BookingSummariesResponse>(`${BASE}/summaries`, { ids });
    },

    /** Who, what, money, attendance and history for one booking. */
    overview(bookingId: string) {
      return client.get<BookingOverviewResponse>(`${BASE}/${bookingId}/overview`);
    },

    /** Invalidate every check-in QR issued so far (leaked or shared ticket). */
    resetTicket(bookingId: string) {
      return client.post<{ rotated: true; meta: RequestMeta }>(
        `${BASE}/${bookingId}/actions/reset_ticket`,
        {},
      );
    },

    create(body: {
      customer_user_id: string;
      business_id: string;
      starts_at: string;
      ends_at: string;
      currency: string;
      business_capability_id?: string | null;
      bookable_resource_id?: string | null;
      staff_user_id?: string | null;
      booking_source_type?: BookingSourceType | null;
      booking_source_id?: string | null;
      allocation_hold_id?: string | null;
      skip_hold?: boolean;
    }) {
      return client.post<{ booking: BookingDto; meta: BookingsListResponse["meta"] }>(
        BASE,
        body,
        newIdempotencyKey(),
      );
    },

    update(
      bookingId: string,
      body: {
        starts_at?: string;
        ends_at?: string;
        staff_user_id?: string | null;
        currency?: string;
      },
    ) {
      return client.patch<{ booking: BookingDto; meta: BookingsListResponse["meta"] }>(
        `${BASE}/${bookingId}`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAction(bookingId: string, action: BookingAction, body: { reason?: string }) {
      return client.post(
        `${BASE}/${bookingId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    addParticipant(
      bookingId: string,
      body: { user_id: string; participant_type: ParticipantType },
    ) {
      return client.post(
        `${BASE}/${bookingId}/participants`,
        body,
        newIdempotencyKey(),
      );
    },

    applyParticipantAction(
      bookingId: string,
      participantId: string,
      action: ParticipantAction,
      body: { reason?: string },
    ) {
      return client.post(
        `${BASE}/${bookingId}/participants/${participantId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    checkConflicts(body: {
      business_id: string;
      starts_at: string;
      ends_at: string;
      business_capability_id?: string | null;
      bookable_resource_id?: string | null;
      exclude_booking_id?: string;
      exclude_hold_id?: string;
    }) {
      return client.post<ConflictCheckResponse>(
        `${BASE}/conflicts/check`,
        body,
        newIdempotencyKey(),
      );
    },

    listHolds(params: HoldsListParams = {}) {
      return client.get<HoldsListResponse>(`${HOLDS}${toQuery(params)}`);
    },

    getHold(holdId: string) {
      return client.get<HoldDetailResponse>(`${HOLDS}/${holdId}`);
    },

    applyHoldAction(
      holdId: string,
      action: HoldAction,
      body: { reason?: string; release_reason_type?: HoldReleaseReasonType },
    ) {
      return client.post(
        `${HOLDS}/${holdId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    convertHold(
      holdId: string,
      body: {
        payment_success_acknowledged: true;
        reason?: string;
        booking_status?: "initiated" | "confirmed";
      },
    ) {
      return client.post<{
        booking: BookingDto;
        hold: HoldDto;
        meta: BookingsListResponse["meta"];
      }>(
        `${HOLDS}/${holdId}/convert`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type BookingsApi = ReturnType<typeof createBookingsApi>;
